import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "../../constant/api";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { ClipLoader } from "react-spinners";
import { comingSoon, pendingLogo } from "../../assets/images";
import { formatAmount } from "../../utils/wordFunctions";
import { depCoins, getNetworkLabel } from "../../constant";
import { Helmet } from "react-helmet-async";
import GetRates from "../../components/utils/GetRates";
import GetTickers from "../../components/utils/GetTickers";
import PageAvatar from "../../components/PageAvatar";
import BusinessBrandHeader from "../../components/customer/BusinessBrandHeader";
import CustomerPageShell from "../../components/customer/CustomerPageShell";

const SinglePayment = () => {
  const { id } = useParams<{ id: string }>();
  const [fullname, setFullname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [tel, setTel] = useState<string>("");
  const [usdAmount, setUsdAmount] = useState<string>("");
  const navigate = useNavigate();
  const [displayUsdAmount, setDisplayUsdAmount] = useState<string>(""); // Stores formatted USD amount
  const [coinName, setCoinName] = useState<string>("");
  const isNumber = (str: string) => /^\d*\.?\d*$/.test(str); // Allows numbers and decimals
  const [depositNetworks, setDepositNetworks] = useState<any[]>([]);
  const [depositNetworkName, setDepositNetworkName] = useState<string>("");
  const [noWallet, setNoWallet] = useState<boolean>(false);

  // Get rates for calculations (prefer selected coin's NGN rate; fallback to USDT)
  const selectedCoinRate = GetRates(coinName || "USDT");
  const { isLoading: isLoadingRates } = GetTickers();

  // Calculate Naira equivalent
  const calculateNairaValue = () => {
    if (!usdAmount || !selectedCoinRate) return null;
    const usd = Number(usdAmount);
    if (isNaN(usd) || usd <= 0) return null;
    return usd * selectedCoinRate;
  };

  const nairaValue = calculateNairaValue();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, ""); // Remove commas for parsing
    if (isNumber(value)) {
      setUsdAmount(value); // Store raw amount
      setDisplayUsdAmount(value); // Temporarily show raw input while typing
    }
  };

  // Debounce formatting to run when the user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      if (usdAmount) {
        const formattedValue = Number(usdAmount).toLocaleString("en-US");
        setDisplayUsdAmount(formattedValue); // Set formatted value after debounce
      }
    }, 300); // 500ms debounce time

    return () => clearTimeout(handler); // Cleanup on re-render
  }, [usdAmount]);

  const axiosInstance = useAuthAxios();
  const getSinglePayment = async () => {
    const response = await axiosInstance.get(
      API.getSinglePaymentPage(id ?? "")
    );
    return response?.data;
  };

  const {
    data: singlePayment,
    error: error1,
    isLoading: loading1,
    // refetch: refetch1,
  } = useQuery({
    // The reference belongs in the key: without it every payment link in a
    // session shares one cache entry, so opening a second link renders the
    // first one's name, amount and merchant until the refetch lands.
    queryKey: ["get-single-payment", id],
    queryFn: getSinglePayment,
    enabled: !!id,
    retry: 1,
  });

  //deposit addy
  // console.log(singlePayment);
  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);

  const name = singlePayment?.data?.page?.name;
  const description = singlePayment?.data?.page?.description;
  const paymentAmount = singlePayment?.data?.page?.amount;
  const allowData = singlePayment?.data?.page?.collectCustomerData;
  const redirectLink = singlePayment?.data?.page?.redirectLink;
  const thumbnailUrl = `${
    singlePayment?.data?.page?.image ? singlePayment?.data?.page?.image : ""
  }`;

  // The public page response carries the merchant alongside the page itself.
  const business = singlePayment?.data?.business;

  /**
   * The merchant leads: their logo and name are what the customer needs to
   * recognise before paying. A business with no logo yet falls back to the
   * page's own image, so the header is never empty — and where the header is
   * already showing the page, the row inside the card is dropped rather than
   * saying the same thing twice.
   */
  const brandName = business?.name || name;
  const brandLogo = business?.logo || thumbnailUrl;
  const showPageRow = !!business?.name;

  const validate = () => {
    if (allowData) {
      if (!fullname.trim()) {
        toast.error("Kindly enter your full name");
        return false;
      }
      if (!email.trim()) {
        toast.error("Kindly enter your email");
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Kindly enter a valid email address");
        return false;
      }
      if (!tel.trim()) {
        toast.error("Kindly enter your phone number");
        return false;
      }
      if (tel.length < 11) {
        toast.error("Kindly enter a valid phone number");
        return false;
      }
    }

    if (!paymentAmount && !usdAmount.trim()) {
      toast.error("Please enter an amount to proceed");
      return false;
    }

    if (!paymentAmount && (!coinName || !depositNetworkName)) {
      toast.error("Please select a coin and network to proceed");
      return false;
    }

    return true;
  };

  // console.log(singlePayment);
  const getDepositAddy = (coin: string, network: string) => {
    // Helper to normalize the network input
    const normalizeNetwork = (net: string) =>
      net.includes("-")
        ? net.replace(/-/g, "").toLowerCase()
        : net.toLowerCase();

    const getDepositWalletAddress = () => {
      const walletAddresses = singlePayment?.data?.page?.wallets;
      if (walletAddresses) {
        const selectedCoin = depCoins.find((c: any) => c.abbr === coin);
        const keysToTry = [coin.toLowerCase()];
        if (selectedCoin?.aliases) {
          for (const alias of selectedCoin.aliases) {
            keysToTry.push(alias.toLowerCase());
          }
        }

        let coinWallet = null;
        for (const key of keysToTry) {
          if (walletAddresses[key]) {
            coinWallet = walletAddresses[key];
            break;
          }
        }

        if (coinWallet) {
          const normalizedNetwork = normalizeNetwork(network);
          const networkWallet = coinWallet.addresses.find(
            (wallet: { network: string }) =>
              wallet.network.toLowerCase() === normalizedNetwork
          );

          if (networkWallet) {
            return networkWallet.address;
          }
        }
      }
      return null;
    };

    return getDepositWalletAddress(); // Return the wallet address
  };

  // console.log(singlePayment);

  const handleProceedPayment = () => {
    if (!singlePayment?.data?.page?.wallets) {
      setNoWallet(true);
    } else {
      setNoWallet(false);
      if (validate()) {
        // Proceed with payment logic
        // Convert USD to Naira for the amount field (used in deposit page)
        const amountInNaira =
          paymentAmount ||
          (usdAmount ? Number(usdAmount) * (selectedCoinRate || 0) : 0);

        navigate(`/payment-page/${id}/deposit`, {
          state: {
            email: email,
            allowData: allowData,
            amount: amountInNaira.toString(),
            paymentAmount: paymentAmount,
            fullname: fullname,
            phone: tel,
            coinName: coinName,
            networkName: depositNetworkName,
            name: name,
            redirectLink: redirectLink,
            description: description,
            walletAddy: getDepositAddy(coinName, depositNetworkName),
            thumbnailUrl: thumbnailUrl,
          },
        });
      }
    }
  };

  // console.log(
  //   getDepositAddy(coinName, depositNetworkName),
  //   "coinName",
  //   coinName,
  //   "depositNetworkName",
  //   depositNetworkName
  // );

  useEffect(() => {
    if (
      !getDepositAddy(coinName, depositNetworkName) &&
      coinName &&
      depositNetworkName &&
      singlePayment?.data?.page?.wallets
    ) {
      toast.error(
        "Wallet address not found for this coin and network, kindly refresh the page and try again"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinName, depositNetworkName, singlePayment?.data?.page?.wallets]);

  return (
    <>
      <Helmet>
        <title>{name ? `${name}${brandName && brandName !== name ? ` · ${brandName}` : ""}` : "Payment page"}</title>
        <meta property="og:title" content={`${name}`} />
        <meta property="og:description" content={`${description}`} />
        <meta property="og:image" content={thumbnailUrl} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={thumbnailUrl} />
      </Helmet>

      <CustomerPageShell>
        {loading1 ? (
          <div className="flex min-h-[60vh] w-full items-center justify-center">
            <ClipLoader color="#0B84CB" size={56} />
          </div>
        ) : (
          <>
            {singlePayment?.data?.page ? (
              <div className="w-full">
                <BusinessBrandHeader
                  name={brandName}
                  logo={brandLogo}
                  tagline={showPageRow ? undefined : description}
                />

                {/* Form card */}
                <div className="mt-8 overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                  {/* What is being paid for, when the header is showing the
                      business rather than this page. */}
                  {showPageRow && (
                    <div className="flex items-center gap-3.5 border-b border-gray-100 bg-gray-50/60 px-5 py-4 md:px-6">
                      <PageAvatar
                        name={name}
                        image={thumbnailUrl}
                        className="size-10"
                        textClassName="text-[15px]"
                      />
                      <div className="min-w-0">
                        <h2 className="truncate font-sora text-[15px] font-semibold text-gray-900">
                          {name}
                        </h2>
                        {description && (
                          <p className="mt-0.5 line-clamp-2 text-[12.5px] font-medium text-gray-500">
                            {description}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="space-y-5 p-5 md:p-6">
                    {allowData && (
                      <>
                        <div>
                          <label className="text-[13px] font-semibold text-gray-700">
                            Full name
                          </label>
                          <input
                            type="text"
                            placeholder="eg: John Doe"
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-[14px] text-gray-800 outline-none transition focus:border-secondary_button focus:bg-white focus:ring-2 focus:ring-secondary_button/15"
                          />
                        </div>
                        <div>
                          <label className="text-[13px] font-semibold text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            placeholder="eg: johndoe@gmail.com"
                            value={email}
                            onChange={(e) =>
                              setEmail(e.target.value.toLowerCase())
                            }
                            className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-[14px] text-gray-800 outline-none transition focus:border-secondary_button focus:bg-white focus:ring-2 focus:ring-secondary_button/15"
                          />
                        </div>
                        <div>
                          <label className="text-[13px] font-semibold text-gray-700">
                            Phone number
                          </label>
                          <input
                            type="tel"
                            placeholder="eg: 08123456789"
                            value={tel}
                            onChange={(e) => setTel(e.target.value)}
                            className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-[14px] text-gray-800 outline-none transition focus:border-secondary_button focus:bg-white focus:ring-2 focus:ring-secondary_button/15"
                          />
                        </div>
                      </>
                    )}

                    {paymentAmount ? (
                      // A fixed amount is the single most important fact on the
                      // page — it gets read, not filled in, so it's typeset
                      // rather than dressed up as a disabled input.
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4 text-center">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500">
                          Amount to pay
                        </span>
                        <p className="mt-1 font-sora text-[28px] font-bold leading-none tracking-[-0.02em] text-gray-900">
                          ₦{formatAmount(paymentAmount)}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="text-[13px] font-semibold text-gray-700">
                          Amount (USD)
                        </label>
                        <div className="mt-2 flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-4 transition focus-within:border-secondary_button focus-within:bg-white focus-within:ring-2 focus-within:ring-secondary_button/15">
                          <span className="text-[15px] font-semibold text-gray-500">
                            $
                          </span>
                          <input
                            type="text"
                            placeholder="eg: 500.00"
                            value={displayUsdAmount}
                            onChange={handleChange}
                            className="w-full bg-transparent py-3.5 text-[14px] text-gray-800 outline-none"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="text-[13px] font-semibold text-gray-700">
                        Preferred coin
                      </label>
                      <select
                        value={coinName}
                        onChange={(e) => {
                          const selectedCoin = depCoins.find(
                            (coin: any) => coin.abbr === e.target.value
                          );
                          if (selectedCoin) {
                            setDepositNetworks(selectedCoin.networks);
                            setCoinName(selectedCoin.abbr);
                            setDepositNetworkName(selectedCoin.networks[0].name);
                          }
                        }}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-[14px] text-gray-800 outline-none transition focus:border-secondary_button focus:bg-white focus:ring-2 focus:ring-secondary_button/15"
                      >
                        <option value="" disabled>
                          -Select a coin-
                        </option>
                        {depCoins.map((coin: any, index: number) => (
                          <option key={index} value={coin.abbr}>
                            {coin.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-[13px] font-semibold text-gray-700">
                        Network
                      </label>
                      <select
                        value={depositNetworkName}
                        onChange={(e) => setDepositNetworkName(e.target.value)}
                        className="mt-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-[14px] text-gray-800 outline-none transition focus:border-secondary_button focus:bg-white focus:ring-2 focus:ring-secondary_button/15"
                      >
                        <option value="" disabled>
                          -Select a network-
                        </option>
                        {depositNetworks?.map((network: any, index: number) => (
                          <option key={index} value={network.name}>
                            {getNetworkLabel(network.name)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Naira equivalent */}
                    {coinName &&
                      depositNetworkName &&
                      usdAmount &&
                      nairaValue && (
                        <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-[12px] font-medium text-gray-600">
                                Equivalent in Naira
                              </h4>
                              <h4 className="mt-1 text-[18px] font-bold text-gray-800">
                                ₦{formatAmount(nairaValue)}
                              </h4>
                            </div>
                            <div className="text-right">
                              <h4 className="text-[12px] font-medium text-gray-600">
                                Coin selected
                              </h4>
                              <h4 className="mt-1 text-[16px] font-semibold uppercase text-secondary_button">
                                {coinName}
                              </h4>
                            </div>
                          </div>
                          {isLoadingRates && (
                            <div className="mt-2 flex items-center gap-2">
                              <ClipLoader color="#0B84CB" size={12} />
                              <span className="text-[11px] text-gray-500">
                                Calculating rates...
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                    {usdAmount && (!coinName || !depositNetworkName) && (
                      <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-3">
                        <h4 className="text-[12px] font-medium text-yellow-800">
                          💡 Select a coin and network to see the Naira
                          equivalent
                        </h4>
                      </div>
                    )}

                    <button
                      disabled={
                        !coinName ||
                        !depositNetworkName ||
                        !getDepositAddy(coinName, depositNetworkName)
                      }
                      onClick={handleProceedPayment}
                      className={
                        !coinName ||
                        !depositNetworkName ||
                        !getDepositAddy(coinName, depositNetworkName)
                          ? "!mt-7 h-[52px] w-full cursor-not-allowed rounded-xl bg-gray-200 text-[15px] font-semibold text-gray-400"
                          : "!mt-7 h-[52px] w-full rounded-xl bg-secondary_button text-[15px] font-semibold text-white shadow-[0_6px_16px_-6px_rgba(11,132,203,0.6)] transition hover:brightness-110 active:scale-[0.99]"
                      }
                    >
                      Proceed to make payment
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center justify-center rounded-2xl border border-gray-200/70 bg-white px-6 py-16 text-center shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                <img
                  src={comingSoon}
                  alt=""
                  className="h-auto w-[150px] max-w-full"
                />
                <h2 className="mt-6 font-sora text-[17px] font-bold text-gray-900">
                  This payment page isn’t available
                </h2>
                <p className="mt-2 max-w-[300px] text-[13.5px] font-medium text-gray-500">
                  The link may have expired or been removed. Check with the
                  business you were paying.
                </p>
              </div>
            )}
          </>
        )}
      </CustomerPageShell>
      {noWallet && (
        <div className="fixed inset-0  flex  justify-center items-start pt-16 pb-16 overflow-auto bg-black  bg-opacity-80">
          <div
            className={` w-11/12 mds:w-9/12     rounded-xl mx-auto p-6 bg-gray-300  overflow-auto  lgss:w-[45%] xxl:w-[33%] `}
          >
            <div className="w-full flex justify-center px-3 md:px-6 items-center flex-col gap-4">
              <div className="w-[116px] h-[116px]">
                <img
                  src={pendingLogo}
                  className="w-full h-full bg-cover"
                  alt=""
                />
              </div>
              <h4 className="font-sora font-bold text-gray-900 text-center  text-[24px]">
                Wallets are being generated
              </h4>
              <h4 className=" font-medium text-gray-500  text-center text-[15px]">
                Wallets will be generated in a minute, kindly bear with us.
              </h4>
            </div>
            <button
              onClick={() => {
                setNoWallet(false);
              }}
              className="mt-10 bg-secondary_button  flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SinglePayment;
